import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon, BuildingIcon, DriverIcon, UserIcon } from '@hero/web-icons';
import type { WizardPersona } from '@hero/web-hooks';
import { Button } from '../../../ui/Button';
import { SelectableCard } from './SelectableCard';

export interface PersonaStepProps {
  /** Currently staged persona — `null` until the user picks one. */
  value: WizardPersona | null;
  /** Stage a persona without advancing the wizard. */
  onChange: (next: WizardPersona) => void;
  /** Confirm the staged persona and advance to step 2. */
  onContinue: () => void;
}

/**
 * Step 1 of the unified signup wizard. Three selectable cards
 * (`company` / `driver` / `individual_customer`) and an explicit
 * Continue button.
 *
 * UX rationale: tap-to-advance felt jumpy — users couldn't change
 * their mind between options without backtracking. The Continue
 * button is always visible so its position is stable; it stays
 * disabled until a persona is staged.
 */
export function PersonaStep({ value, onChange, onContinue }: PersonaStepProps) {
  const { t } = useTranslation('m-auth');

  const personas: { id: WizardPersona; icon: React.ReactNode }[] = [
    { id: 'company', icon: <BuildingIcon size="medium" /> },
    { id: 'driver', icon: <DriverIcon size="medium" /> },
    { id: 'individual_customer', icon: <UserIcon size="medium" /> },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{t('personas.step_title')}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('personas.step_subtitle')}</p>
      </div>
      <div className="space-y-3">
        {personas.map(p => (
          <SelectableCard
            key={p.id}
            selected={value === p.id}
            onClick={() => onChange(p.id)}
            title={t(`personas.${p.id}.title`)}
            description={t(`personas.${p.id}.description`)}
            icon={p.icon}
          />
        ))}
      </div>

      <div className="pt-2">
        <Button
          type="button"
          onClick={onContinue}
          disabled={value === null}
          className="w-full">
          <span className="inline-flex items-center justify-center gap-2">
            {t('wizard.persona.continue')}
            {/* `rtl:rotate-180` flips the chevron in Arabic so the arrow
                always points "forward" in the reading direction. */}
            <ArrowRightIcon size="small" className="rtl:rotate-180" />
          </span>
        </Button>
      </div>
    </div>
  );
}
